import { apiRequest } from './client';
import type { Task, TaskPriority, TaskStatus } from '../types/admin';

export type CreateTaskPayload = {
  readonly title: string;
  readonly description?: string | null;
  readonly deadline?: string | null;
  readonly priority?: TaskPriority;
  readonly assignee_admin_ids: readonly string[];
  readonly related_patient_id?: string | null;
  readonly related_payment_id?: string | null;
};

export type UpdateTaskPayload = {
  readonly title?: string;
  readonly description?: string | null;
  readonly deadline?: string | null;
  readonly status?: TaskStatus;
  readonly priority?: TaskPriority;
  readonly assignee_admin_ids?: readonly string[];
  readonly related_patient_id?: string | null;
  readonly related_payment_id?: string | null;
};

export type TaskFilters = {
  readonly status?: TaskStatus;
  readonly priority?: TaskPriority;
  readonly mine_only?: boolean;
  readonly related_patient_id?: string;
  readonly search?: string;
};

function buildTaskQuery(filters: TaskFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.mine_only) params.set('mine_only', 'true');
  if (filters.related_patient_id) params.set('related_patient_id', filters.related_patient_id);
  if (filters.search) params.set('search', filters.search);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function listTasks(
  filters: TaskFilters,
  accessToken: string,
): Promise<readonly Task[]> {
  return apiRequest<readonly Task[]>(`/admin/tasks${buildTaskQuery(filters)}`, {
    token: accessToken,
  });
}

export function getTask(id: string, accessToken: string): Promise<Task> {
  return apiRequest<Task>(`/admin/tasks/${id}`, {
    token: accessToken,
  });
}

export function createTask(payload: CreateTaskPayload, accessToken: string): Promise<Task> {
  return apiRequest<Task>('/admin/tasks', {
    method: 'POST',
    body: payload,
    token: accessToken,
  });
}

export function updateTask(
  id: string,
  payload: UpdateTaskPayload,
  accessToken: string,
): Promise<Task> {
  return apiRequest<Task>(`/admin/tasks/${id}`, {
    method: 'PATCH',
    body: payload,
    token: accessToken,
  });
}

export function completeTask(id: string, accessToken: string): Promise<Task> {
  return apiRequest<Task>(`/admin/tasks/${id}/complete`, {
    method: 'POST',
    token: accessToken,
  });
}

export function deleteTask(id: string, accessToken: string): Promise<void> {
  return apiRequest<void>(`/admin/tasks/${id}`, {
    method: 'DELETE',
    token: accessToken,
  });
}
